/**
 * Typed API client — the new entrypoint for frontend HTTP calls.
 *
 * Coexists with the legacy src/api/api-client.js during the JSX → TSX
 * migration. New hooks (src/hooks/queries/*, src/hooks/mutations/*) import
 * from here. The legacy api.entities.* surface remains available until
 * every caller has been migrated and the god-endpoint /api/entities/:Name
 * is removed on the backend (ADR-010).
 *
 * Contract:
 *   - Credentials sent via HttpOnly cookies (no Bearer header).
 *   - 401 triggers a single-flight refresh then retry once; a second 401
 *     redirects to / (see redirectToLogin).
 *   - Structured errors (JwtClaimsV2 backend shape) are thrown as
 *     ApiErrorException with the parsed { code, message, context? }
 *     envelope. Legacy AppError flat errors are also handled — kept
 *     backwards-compatible with the existing backend responses.
 *
 * See REFACTORING_FRONTEND_PLAN.md §14 (error handling) and §15 (auth
 * refresh). Zod parsing of responses is performed by the call sites,
 * not here — this client is content-type agnostic.
 */

import { ApiErrorSchema, type ApiError } from '@revcall/shared';

/**
 * Inline type guard — was previously imported from @revcall/shared but Vite's
 * Rollup bundler can't reliably extract function named exports from the
 * shared package's CJS dist (only types and zod schemas survive the
 * import-analysis). Calling ApiErrorSchema.safeParse directly here keeps
 * the exact same behaviour without the cross-package import surface.
 */
function isStructuredApiError(value: unknown): value is ApiError {
  return ApiErrorSchema.safeParse(value).success;
}

const API_BASE: string = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

/**
 * Raised when the backend returns a non-2xx. Carries the structured
 * envelope when available, or synthesizes one from legacy AppError
 * responses ({ error: string, code?: string }).
 */
export class ApiErrorException extends Error {
  readonly error: ApiError['error'];
  readonly status: number;

  constructor(error: ApiError['error'], status: number) {
    super(error.message);
    this.name = 'ApiErrorException';
    this.error = error;
    this.status = status;
  }

  /** Machine-readable code (e.g. 'JWT_VERSION_OUTDATED', 'QUOTA_EXCEEDED'). */
  get code(): string {
    return this.error.code;
  }
}

// ── Single-flight refresh ──────────────────────────────────────────────────

let refreshPromise: Promise<void> | null = null;

async function refreshOnce(): Promise<void> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
    .then((resp) => {
      if (!resp.ok) throw Object.assign(new Error('Refresh failed'), { status: resp.status });
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  // Always land on the root after a session loss. Returning the user to a
  // deep authenticated URL via ?next= proved confusing in practice — they
  // re-log and are dumped on a sub-route they don't expect.
  window.location.href = '/';
}

// ── Core request ──────────────────────────────────────────────────────────

export interface RequestOptions {
  /** Query parameters — appended to the path. Values are URL-encoded. */
  query?: Record<string, string | number | boolean | null | undefined>;
  /**
   * Request body. A FormData is sent as-is (the browser writes the
   * multipart/form-data boundary itself, so Content-Type is NOT set here);
   * anything else is serialized with JSON.stringify and Content-Type is set
   * for you.
   */
  body?: unknown;
  /** Abort signal for cancellable requests (React Query hook integration). */
  signal?: AbortSignal;
  /** Internal: skip the 401 refresh branch on the retry call. */
  _isRetry?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  if (!query) return `${API_BASE}${path}`;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${API_BASE}${path}?${qs}` : `${API_BASE}${path}`;
}

async function parseErrorEnvelope(resp: Response): Promise<ApiError['error']> {
  let body: unknown;
  try {
    body = await resp.json();
  } catch {
    return { code: 'UNKNOWN', message: resp.statusText || 'Request failed' };
  }

  // New structured envelope: { error: { code, message, context?, issues? } }
  if (isStructuredApiError(body)) return (body as ApiError).error;

  // Legacy flat AppError envelope: { error: string, code?: string }
  if (
    body &&
    typeof body === 'object' &&
    'error' in body &&
    typeof (body as { error: unknown }).error === 'string'
  ) {
    const legacy = body as { error: string; code?: string };
    return { code: legacy.code ?? 'APP_ERROR', message: legacy.error };
  }

  return { code: 'UNKNOWN', message: resp.statusText || 'Request failed' };
}

/** Statuses the app expects and handles: logged at debug level (QA-W7-13). */
export function isExpectedStatus(status: number | undefined): boolean {
  return status === 401 || status === 403;
}

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const isMultipart = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const init: RequestInit = {
    method,
    credentials: 'include',
    // A multipart body carries its own boundary in the Content-Type; setting
    // the header by hand drops the boundary and the server parses nothing.
    headers: isMultipart ? {} : { 'Content-Type': 'application/json' },
    signal: options.signal,
  };
  if (options.body !== undefined) {
    init.body = isMultipart ? (options.body as FormData) : JSON.stringify(options.body);
  }

  const url = buildUrl(path, options.query);
  const t0 = performance.now();
  // eslint-disable-next-line no-console
  console.log(`[client.ts] → ${method} ${path}${options._isRetry ? ' (retry)' : ''}`);
  const resp = await fetch(url, init);
  const dt = Math.round(performance.now() - t0);

  // Auth refresh branch (non-auth paths only, and only once).
  if (resp.status === 401 && !options._isRetry && !path.startsWith('/auth/')) {
    // eslint-disable-next-line no-console
    console.warn(`[client.ts] ← ${method} ${path} 401 (${dt}ms) → attempting refresh`);
    try {
      await refreshOnce();
      // eslint-disable-next-line no-console
      console.log('[client.ts] refresh OK, retrying original request');
    } catch (err) {
      // A refresh refused for load (429) or a server/network error says
      // nothing about the session: keep the user on the page (RUI-12/13).
      const status = (err as { status?: unknown })?.status;
      if (typeof status !== 'number' || status === 429 || status >= 500) {
        // eslint-disable-next-line no-console
        console.warn('[client.ts] refresh unavailable (', status ?? 'network', ') → no redirect');
        // i18n-exempt: developer message; the screen translates by the error code
        throw new ApiErrorException({ code: 'SESSION_CHECK_UNAVAILABLE', message: 'Session check unavailable' }, typeof status === 'number' ? status : 503);
      }
      // eslint-disable-next-line no-console
      console.error('[client.ts] refresh FAILED → redirect to login');
      redirectToLogin();
      throw new ApiErrorException(
        // i18n-exempt: fallback text only; the code is what gets translated (common:errors)
        { code: 'SESSION_EXPIRED', message: 'Session expired' },
        401,
      );
    }
    return request<T>(method, path, { ...options, _isRetry: true });
  }

  if (!resp.ok) {
    const envelope = await parseErrorEnvelope(resp);
    // QA-W7-13: 401 and 403 are answers the screens handle (not signed in
    // yet, SUPERVISION_DISABLED, PERMISSION_DENIED…): debug level, so the
    // console's errors stay for what is actually broken.
    // eslint-disable-next-line no-console
    (isExpectedStatus(resp.status) ? console.debug : console.error)(`[client.ts] ← ${method} ${path} ${resp.status} (${dt}ms)`, envelope);
    // Special case: JWT v2 rejected a v1 token — a force-logout requires
    // a clean redirect to /login.
    if (envelope.code === 'JWT_VERSION_OUTDATED') {
      redirectToLogin();
    }
    throw new ApiErrorException(envelope, resp.status);
  }

  // eslint-disable-next-line no-console
  console.log(`[client.ts] ← ${method} ${path} ${resp.status} (${dt}ms)`);

  // 204 No Content — return undefined as T.
  if (resp.status === 204) return undefined as unknown as T;

  const text = await resp.text();
  return (text ? JSON.parse(text) : null) as T;
}

// ── Shared GET /auth/me (QA-W4-1) ─────────────────────────────────────────
//
// Three readers ask for the session on every page load: the legacy
// AuthContext, the authStore (AuthBootstrap, right after) and the dialer
// (callerMe). Each sent its own GET /auth/me. One request now serves them:
// a call in flight is shared, and its answer is reused for ME_REUSE_MS. Any
// write under /auth/ (login, logout, refresh, organisation switch, settings
// save) forgets it, so a change is never read from the old copy.

export const ME_REUSE_MS = 5000;
let meInFlight: Promise<unknown> | null = null;
let meCache: { at: number; json: string } | null = null;

/** Forget the shared /auth/me answer (tests, and every write under /auth/). */
export function forgetSharedMe(): void {
  meInFlight = null;
  meCache = null;
}

function sharedMe<T>(): Promise<T> {
  // A fresh copy for each reader: one of them mutating it cannot leak.
  if (meCache && Date.now() - meCache.at < ME_REUSE_MS) return Promise.resolve(JSON.parse(meCache.json) as T);
  if (!meInFlight) {
    const p: Promise<unknown> = request<unknown>('GET', '/auth/me')
      .then((data) => {
        if (meInFlight === p) meCache = { at: Date.now(), json: JSON.stringify(data ?? null) };
        return data;
      })
      .finally(() => { if (meInFlight === p) meInFlight = null; });
    meInFlight = p;
  }
  return meInFlight.then((data) => JSON.parse(JSON.stringify(data ?? null)) as T);
}

function isSharedMe(path: string, options?: RequestOptions): boolean {
  return path === '/auth/me' && !options?.signal && !options?.query;
}

// ── Public surface ────────────────────────────────────────────────────────

function write<T>(method: 'POST' | 'PATCH' | 'PUT' | 'DELETE', path: string, options: RequestOptions = {}): Promise<T> {
  if (path.startsWith('/auth/')) forgetSharedMe();
  return request<T>(method, path, options);
}

export const apiClient = {
  get: <T>(path: string, options?: RequestOptions) => (isSharedMe(path, options) ? sharedMe<T>() : request<T>('GET', path, options)),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) =>
    write<T>('POST', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) =>
    write<T>('PATCH', path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) =>
    write<T>('PUT', path, { ...options, body }),
  delete: <T>(path: string, options?: RequestOptions) => write<T>('DELETE', path, options),
};

export type ApiClient = typeof apiClient;
